import React, { useState, useEffect } from 'react';
import { Mail, Send, MessageCircle, Phone } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient, publicApi } from '../context/AuthContext';
import { useTurnstile } from '../components/Turnstile';
import useSEO from '../hooks/useSEO';
import { useLanguage } from '../context/LanguageContext';
import useSEOPage from '../hooks/useSEOPage';

export default function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();
  const { getToken } = useTurnstile();
  const [contactSettings, setContactSettings] = useState({
    contact_email: 'contact@joeycyan.com',
    contact_phone: '+33 7 58 90 70 21',
    whatsapp_number: '+33758907021'
  });

  // Load SEO data for contact page
  const { seoData } = useSEOPage('contact');
  
  // Apply SEO meta tags
  useSEO({
    title: seoData?.meta_title,
    description: seoData?.meta_description,
    ogImage: seoData?.og_image,
    url: 'https://joeycyan.com/contact'
  });

  // Load contact settings
  useEffect(() => {
    const fetchContactSettings = async () => {
      try {
        const { data } = await publicApi.get('/settings/contact');
        setContactSettings(data);
      } catch (error) {
        console.error('Error loading contact settings:', error);
      }
    };
    fetchContactSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const ttoken = await getToken();
      await apiClient.post('/contact', formData, {
        headers: ttoken ? { 'X-Turnstile-Token': ttoken } : {}
      });
      toast.success(t('contact.success'));
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast.error(t('contact.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50 py-12 px-4 md:px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
            {t('contact.title')}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Contact Methods */}
          <div className="lg:col-span-1 space-y-6">
            {/* Email Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-full bg-[#00aaff]/10 flex items-center justify-center mb-4">
                <Mail className="text-[#00aaff]" size={24} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t("contact.emailCard")}</h3>
              <p className="text-sm text-gray-600 mb-3">
                {t('contact.emailDesc')}
              </p>
              <a 
                href={`mailto:${contactSettings.contact_email}`}
                className="text-[#00aaff] font-semibold text-sm hover:underline inline-flex items-center gap-1"
              >
                {contactSettings.contact_email}
              </a>
            </div>

            {/* WhatsApp Card */}
            <a 
              href={`https://wa.me/${contactSettings.whatsapp_number}?text=Bonjour,%20j'ai%20une%20question%20concernant%20Joey%20Cyan`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all transform hover:scale-105 block group"
            >
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mb-4">
                <MessageCircle className="text-white" size={24} />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{t("contact.whatsapp")}</h3>
              <p className="text-sm text-white/90 mb-3">
                {t('contact.whatsappDesc')}
              </p>
              <span className="text-white font-semibold text-sm inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                {t('contact.openWhatsapp')}
                <Send size={16} />
              </span>
            </a>

            {/* Phone Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-full bg-[#00aaff]/10 flex items-center justify-center mb-4">
                <Phone className="text-[#00aaff]" size={24} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t("contact.phoneCard")}</h3>
              <p className="text-sm text-gray-600 mb-3">
                {t('contact.phoneDesc')}
              </p>
              <a 
                href={`tel:${contactSettings.contact_phone.replace(/\s/g, '')}`}
                className="text-[#00aaff] font-semibold text-sm hover:underline"
              >
                {contactSettings.contact_phone}
              </a>
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{t("contact.sendMessage")}</h2>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* First Name & Last Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                      {t('contact.firstName')} *
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                      placeholder="Jean"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                      {t('contact.lastName')} *
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      required
                      placeholder="Dupont"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                    />
                  </div>
                </div>

                {/* {t('contact.email')} */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                    {t('contact.email')} *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="jean.dupont@example.com"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+33 6 12 34 56 78"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                    {t('contact.subject')} *
                  </label>
                  <select
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                  >
                    <option value="">{t('contact.selectSubject')}</option>
                    <option value="Produits">{t('contact.subjectProducts')}</option>
                    <option value="Commande">{t('contact.subjectOrder')}</option>
                    <option value="SAV">{t('contact.subjectSupport')}</option>
                    <option value="Partenariat">{t('contact.subjectPartnership')}</option>
                    <option value="Autre">{t('contact.subjectOther')}</option>
                  </select>
                </div>

                {/* {t('contact.message')} */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                    {t('contact.message')} *
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows={6}
                    placeholder="Décrivez votre demande en détail..."
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors resize-none"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#00aaff] text-white py-4 rounded-full font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#0088dd] transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-[#00aaff]/20"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send size={18} />
                      {t('contact.send')}
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
